import type {
	PersonaFields,
	PromptGeneratorFramework,
} from "@repo/core/client";

/**
 * Copy for /free-ai-visibility-prompt-generator with Personas, as
 * approved in the design lab (marketing-prompt-generator-personas, which now
 * reads it from here). English only: the page ships EN only. No em dashes.
 * Headings keep the page's Title Case.
 */

export const HERO = {
	eyebrow: "Free Tool",
	title: "AI Visibility Prompt Generator",
	lede: "Get the prompts real customers ask ChatGPT, Gemini, and Claude about your category, and meet the people asking them. We build both from your website, using six proven marketing frameworks.",
};

export const META = {
	title: "Free AI Visibility Prompt Generator | Spyglasses",
	description:
		"Generate the prompts real customers use to research your category in ChatGPT, Gemini, and Claude, plus personas built from your site. Six frameworks. Free, no signup.",
};

/**
 * Form-level framework descriptions. Replaces the core
 * PROMPT_GENERATOR_FRAMEWORK_DESCRIPTIONS strings, two of which use em
 * dashes and one of which says "CIO, CFO" where the page says "CTO, CFO".
 */
export const FRAMEWORK_FORM_DESCRIPTIONS: Record<
	PromptGeneratorFramework,
	string
> = {
	original:
		"Classic buyer-intent prompts: category best, use case, budget, comparison, and segment.",
	category_entry_points:
		"The triggers and cues that send buyers into your category, before they think of any brand.",
	jobs_to_be_done:
		"The functional, emotional, and social jobs your buyers are trying to get done.",
	buyers_journey:
		"Awareness, consideration, and decision prompts, from the first problem to the final vendor pick.",
	stakeholder_perspectives:
		"Prompts written from the point of view of each person in the buying decision.",
	brand_prompts:
		"Questions people ask about your brand by name: who you are, what you offer, and whether to trust you.",
};

export const PERSONA_OPTION = {
	label: "Personas",
	title: "Draft personas from your site",
	body: "We'll add 3 to 5 different people who'd ask these questions, drafted from who your site serves, so you can see who's asking.",
};

export const STEPS = [
	{
		step: "1",
		title: "Enter Your Domain",
		description:
			"We read up to five pages of your site and build a brand snapshot: your category, audience, customer segments, and what sets you apart.",
	},
	{
		step: "2",
		title: "Pick Your Frameworks",
		description:
			"Choose any of six proven frameworks. Keep Personas on to also draft the people who ask AI about your category.",
	},
	{
		step: "3",
		title: "Get Prompts and Personas",
		description:
			"Up to 60 prompts plus 3 to 5 personas. Copy them, export a CSV, or email yourself a copy.",
	},
	{
		step: "4",
		title: "Track Them Daily",
		description:
			"Sign up and we'll import everything for you, then track how AI answers your prompts and each persona, every day.",
	},
];

export const FRAMEWORK_CARDS = [
	{
		title: "Category Entry Points",
		description:
			"Based on Ehrenberg-Bass Institute research. Finds the triggers, cues, and occasions that pull buyers into your category, before they think about any brand.",
		example:
			"“We just switched to remote work. What collaboration tools should we look into?”",
	},
	{
		title: "Jobs to Be Done",
		description:
			"Frames prompts around the functional, emotional, and social jobs buyers are trying to get done. Outcomes over features.",
		example:
			"“How can I send invoices faster without switching accounting systems?”",
	},
	{
		title: "Buyer's Journey",
		description:
			"Covers the awareness, consideration, and decision stages, from the first sign of a problem to the final vendor pick.",
		example:
			"“What are the different ways to manage customer feedback at scale?”",
	},
	{
		title: "Stakeholder Perspectives",
		description:
			"Finds 3 to 5 people in the buying decision and writes prompts from each one's point of view: the CFO, the IT lead, the daily user.",
		example:
			"“As a CFO, what's the total cost of owning an enterprise CRM?”",
	},
	{
		title: "Brand Prompts",
		description:
			"The questions people ask about you by name: who you are, what you sell, what you cost, and how you compare.",
		example: "“Is Acme a good fit for a 200-person company?”",
	},
	{
		title: "Standard AI Visibility",
		description:
			"Classic buyer-intent prompts covering category best, use case, budget, comparison, segment, and location.",
		example:
			"“What are the best project management tools for remote teams?”",
	},
];

export const DESCRIPTIVE = {
	title: "The prompts buyers actually use to find you",
	paragraphs: [
		"AI visibility starts with the questions real buyers type into ChatGPT, Claude, and Perplexity, not the keywords they'd enter in Google. This generator builds those questions from proven demand frameworks: Category Entry Points (the Ehrenberg-Bass triggers that pull buyers into your category), Jobs to Be Done (the outcomes they're after), the Buyer's Journey (awareness through decision), Stakeholder Perspectives, and Brand Prompts about you by name.",
		"The same question gets a different answer depending on who asks it. A Medicare enrollee in Syracuse and a parent managing three prescriptions want different things from a pharmacy, just as a CFO and an IT lead want different things from software, and AI shapes its recommendations to fit. So the generator also drafts personas from your site: who they are, where they are, what they already have, and what matters most to them. Open any persona to see exactly what we send AI as that person.",
		"Built from a snapshot of your own site, the prompts and personas become the tracking set you measure against: the same questions an AI visibility platform runs every day to show where you appear, where competitors win, and which people AI steers somewhere else.",
	],
	takeaways: [
		"Buyers ask AI in plain language about jobs and problems, so visibility starts from their questions, not your keywords.",
		"Category Entry Points, Jobs to Be Done, and the Buyer's Journey each show a different layer of buying intent.",
		"Personas keep a prompt word for word and change who's asking, which shows how answers shift from one person to the next.",
		"Prompts and personas come from a snapshot of your own site, so they match your real category, segments, and audience.",
		"The results double as your tracking list: sign up and they're imported for daily tracking.",
	],
};

export const PERSONAS_BAND = {
	eyebrow: "New: Personas",
	title: "Same question. Different person. Different answer.",
	body: "Personas keep your prompt exactly as written and change who's asking. We add one short line about the person in front of the question, the way people give AI context, so you can see who AI recommends you to.",
	points: [
		"Drafted from who your site serves",
		"3 to 5 different people: individuals, people at organizations, or both",
		"Included in your CSV and your email copy",
	],
};

export const CLOSING_CTA = {
	title: "Track Your Prompts and Personas",
	body: "Sign up for a free Spyglasses account and we'll import your prompts and personas. We run your prompts against ChatGPT, Gemini, and Google AI Overviews every day and ask ChatGPT and Gemini as each persona, so you can measure and grow your AI visibility with each of them.",
	primary: "Track These Personas",
	secondary: "See Pricing",
};

// Single source for the FAQ: feeds both the visible FAQSection and the JSON-LD.
export const FAQ_ITEMS = [
	{
		question: "What is an AI visibility prompt?",
		answer: "An AI visibility prompt is a question potential customers might type into AI assistants like ChatGPT, Claude, or Perplexity when researching solutions in your category. Once you know these prompts, you can shape your content to show up in the answers.",
	},
	{
		question: "What frameworks does the prompt generator use?",
		answer: "Six proven frameworks: Standard AI Visibility (buyer-intent patterns), Category Entry Points (buying triggers and cues), Jobs to Be Done (functional, emotional, and social jobs), Buyer's Journey (awareness, consideration, and decision), Stakeholder Perspectives (each decision maker's point of view), and Brand Prompts (questions people ask about your brand by name).",
	},
	{
		question: "What are personas?",
		answer: "Personas are short profiles of the people who ask AI about your category. A persona can be a person asking for themselves, like “I'm a Medicare enrollee in Syracuse, New York. I'm 74 and live alone,” or someone asking for their organization, like “I'm the CFO at a 300-person consulting firm; we use NetSuite.” Each one becomes a single line we put in front of a prompt. Stakeholder Perspectives rewrites the question from a role's point of view. Personas keep your question exactly as written and change who's asking, so you can compare answers person by person.",
	},
	{
		question: "How do you build personas from my site?",
		answer: "We read up to five pages of your site and work out who asks AI about what you offer: individuals, people at organizations, or both. Then we draft 3 to 5 different people who'd ask these questions, like a retiree and a busy parent choosing a pharmacy, or a CFO and an IT lead choosing software. Each persona includes what matters most to them and the constraints that shape what they'd ask.",
	},
	{
		question: "What does “Track these personas” do?",
		answer: "It starts a free Spyglasses account with your results already in it. After you sign up, you pick the prompts and personas to keep, and we can set up a project that asks each prompt as each persona. We track the answers daily, so you can see which personas AI recommends you to and which ones it points to someone else.",
	},
	{
		question: "How many prompts does it generate?",
		answer: "Up to 10 prompts per framework, so up to 60 with all six selected. With Personas on, you also get 3 to 5 personas.",
	},
	{
		question: "Do I need an account?",
		answer: "No. The prompt generator is free and needs no signup. Copy your prompts and personas, export them as CSV, or enter your email to get a copy. The email is optional. We won't add you to an email list, and you can regenerate your results any time with the same domain.",
	},
	{
		question: "How does it understand my brand?",
		answer: "We read up to five pages of your website and build a brand snapshot: your category, target audience, customer segments, problems solved, differentiators, pricing model, and more. That snapshot powers every prompt and persona.",
	},
];

/** "Email me this panel". One email per generation; the address is never kept. */
export const EMAIL_PANEL = {
	fieldLabel: "Email me this panel",
	optional: "(optional)",
	intro: "Get a copy of these prompts and personas in your inbox.",
	/** Shown under the field. The address is used for this email only. */
	privacyNote:
		"We won't add you to an email list. You can regenerate these results any time with the same domain.",
	placeholder: "you@company.com",
	submit: "Email me",
	sending: "Sending...",
	sentMessage: "Sent. Check your inbox for your prompts and personas.",
	sentTo: (address: string) => `Sent to ${address}.`,
	errorBadge: "Not sent",
	errorMessage:
		"We couldn't send that email. Check the address and try again.",
	alreadySentBadge: "Already sent",
	alreadySentMessage:
		"We already emailed this panel. Generate again with the same domain any time for a fresh copy.",
};

/** Progress rows for the generating state (index = step). */
export const GENERATING_STEPS: readonly string[] = [
	"Reading your site",
	"Building your brand snapshot",
	"Writing prompts for each framework",
	"Drafting your personas",
];

/**
 * The "same question, different person" examples in the Personas band: a
 * local pharmacy asked about by two people, beside a software category
 * asked about by two people at one company. Static page copy, so the band
 * reads the same for every visitor. Drafts never carry a name.
 */
export const PERSONAS_BAND_EXAMPLES: ReadonlyArray<{
	key: string;
	/** Who asks, in a few words ("People choosing a local pharmacy"). */
	audience: string;
	query: string;
	personas: ReadonlyArray<PersonaFields & { key: string }>;
}> = [
	{
		key: "pharmacy",
		audience: "People choosing a local pharmacy",
		query: "What's the best pharmacy near me for prescription refills?",
		personas: [
			{
				key: "medicare",
				askerType: "individual",
				name: null,
				title: "Medicare enrollee",
				roleGroup: null,
				seniority: null,
				decisionRole: null,
				segment: "Medicare households",
				companySize: null,
				industry: null,
				region: "Syracuse, New York",
				household: "I'm 74 and live alone.",
				techStack: ["a Part D plan"],
				priorities: ["Low copays on four monthly prescriptions"],
				constraints: ["No driving after dark"],
				contextOverride: null,
			},
			{
				key: "parent",
				askerType: "individual",
				name: null,
				title: "Parent managing family prescriptions",
				roleGroup: null,
				seniority: null,
				decisionRole: null,
				segment: "Families",
				companySize: null,
				industry: null,
				region: "Syracuse, New York",
				household:
					"I manage three prescriptions for my two kids and me.",
				techStack: [],
				priorities: ["All three refills ready on the same day"],
				constraints: ["Pickup only after work"],
				contextOverride: null,
			},
		],
	},
	{
		key: "software",
		audience: "A company choosing software",
		query: "What is the best expense management software for a growing company?",
		personas: [
			{
				key: "cfo",
				askerType: "professional",
				name: null,
				title: "CFO",
				roleGroup: "finance",
				seniority: "c_level",
				decisionRole: "economic_buyer",
				segment: "Mid-market professional services firms",
				companySize: "201-500",
				industry: "Professional services",
				region: "United States",
				household: null,
				techStack: ["NetSuite"],
				priorities: ["Close the books faster"],
				constraints: ["Budget is set for the year"],
				contextOverride: null,
			},
			{
				key: "it-lead",
				askerType: "professional",
				name: null,
				title: "Director of IT",
				roleGroup: "it",
				seniority: "director",
				decisionRole: "technical_evaluator",
				segment: "Mid-market professional services firms",
				companySize: "201-500",
				industry: "Professional services",
				region: "United States",
				household: null,
				techStack: ["Okta", "Google Workspace"],
				priorities: ["One login for every tool"],
				constraints: ["Every new tool passes a security review"],
				contextOverride: null,
			},
		],
	},
];
